import React from "react";

import {
  faCheckCircle,
  faClock,
  faWarning,
} from "@fortawesome/pro-solid-svg-icons";
import { twJoin } from "tailwind-merge";

import { NodeRunStateV2 } from "src/constants/NodeDataTypes";
import { Icon } from "src/design-system/Icon";
import { JobRunStatusType } from "src/jobs/types";

type IconProps = { marginLeft?: boolean };

const ErroredIcon: React.FC<IconProps> = ({ marginLeft = false }) => (
  <div
    className={twJoin("flex h-4 items-center", marginLeft && "ml-1")}
    data-loc="run-icon-error"
  >
    <Icon icon={faWarning} size="2xs" variant="error" />
  </div>
);
const SuccessfulIcon: React.FC<IconProps> = ({ marginLeft = false }) => (
  <div
    className={twJoin("flex h-4 items-center", marginLeft && "ml-1")}
    data-loc="run-icon-success"
  >
    <Icon icon={faCheckCircle} size="2xs" variant="success" />
  </div>
);
const PendingIcon: React.FC<IconProps> = ({ marginLeft = false }) => (
  <div
    className={twJoin("flex h-4 items-center", marginLeft && "ml-1")}
    data-loc="run-icon-pending"
  >
    <Icon icon={faClock} size="2xs" variant="active" />
  </div>
);

type Props = {
  runStateV2: NodeRunStateV2 | undefined;
  marginLeft?: boolean;
  jobRunCounts?: { success: number; failed: number };
};

export const NodeRunIconV2: React.FC<Props> = ({
  runStateV2,
  marginLeft = false,
  jobRunCounts,
}) => {
  const showNothing =
    !runStateV2 ||
    runStateV2.type === "not-run" ||
    (runStateV2.type === "job-run" &&
      !jobRunCounts &&
      runStateV2.jobRun.status.type !== JobRunStatusType.Failed);

  const showPending = runStateV2?.type === "historical-pending";

  const showError =
    runStateV2?.type === "test-error" ||
    runStateV2?.type === "historical-error" ||
    (runStateV2?.type === "job-run" &&
      runStateV2.jobRun.status.type === JobRunStatusType.Failed) ||
    (runStateV2?.type === "job-run" && (jobRunCounts?.failed ?? 0) > 0) ||
    (runStateV2?.type === "test-run" &&
      runStateV2.testResult.failure_count > 0);

  if (showNothing) return <></>;
  if (showPending) return <PendingIcon marginLeft={marginLeft} />;
  if (showError) return <ErroredIcon marginLeft={marginLeft} />;
  return <SuccessfulIcon marginLeft={marginLeft} />;
};

export const GroupNodeRunIcon: React.FC<{
  runStates: (NodeRunStateV2 | undefined)[];
  nodeHasJobRunFailures?: boolean;
  nodeHasJobRunCounts?: boolean;
}> = ({ runStates, nodeHasJobRunFailures, nodeHasJobRunCounts }) => {
  const erroredRunState = runStates.find(
    (runState) =>
      runState?.type === "test-error" ||
      runState?.type === "historical-error" ||
      (runState?.type === "job-run" &&
        runState.jobRun.status.type === JobRunStatusType.Failed),
  );
  const childrenNodeHasFailures =
    nodeHasJobRunFailures ||
    runStates.find(
      (runState) =>
        runState?.type === "test-run" &&
        (runState.testResult.failure_count > 0 ||
          runState.testResult.ignored_count > 0),
    );
  if (erroredRunState || childrenNodeHasFailures) {
    return <ErroredIcon />;
  } else if (
    runStates.some(
      (runState) =>
        runState &&
        runState.type !== "not-run" &&
        (runState.type !== "job-run" || nodeHasJobRunCounts),
    )
  ) {
    return <SuccessfulIcon />;
  }
  return <></>;
};
